/**
 * MyGiftCard — iter1118b
 *
 * Малка карта за секция „Абонамент" в профила. Показва остатъчни
 * кредити (% от подаръка), оставащи дни от пробния период и дневен
 * лимит. Ако потребителят няма активен подарък — не рендира нищо.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Gift, Clock, Zap, CalendarClock } from "lucide-react";

export default function MyGiftCard({ isDark }) {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/gift/me`);
        setData(r.data);
      } catch { /* тих провал */ }
      setLoading(false);
    })();
  }, [API]);

  if (loading || !data || !data.has_gift) return null;

  const pct = data.credits_pct ?? 0;
  const cardCls = isDark
    ? "bg-gradient-to-br from-amber-900/30 to-orange-900/20 border border-amber-700/40"
    : "bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300";
  const textMain = isDark ? "text-amber-100" : "text-amber-900";
  const textSub = isDark ? "text-amber-300/80" : "text-amber-700";
  const barBg = isDark ? "bg-amber-900/40" : "bg-amber-200";
  const barFill = pct > 20 ? "bg-amber-500" : "bg-rose-500";

  return (
    <div className={`rounded-2xl p-5 ${cardCls}`} data-testid="my-gift-card">
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-5 h-5" />
        <h3 className={`font-bold text-sm ${textMain}`}>Моят подарък</h3>
        <span className={`text-[11px] ${textSub} ml-auto font-mono`}>{data.code}</span>
      </div>

      {/* Прогрес бар на кредитите */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1.5">
          <span className={textSub}>Остатък от бонус кредитите</span>
          <span className={`${textMain} font-semibold`}>
            {data.credits_current} / {data.credits_initial}
          </span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${barBg}`}>
          <div className={`h-full ${barFill} transition-all duration-500`}
               style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </div>

      {/* Три компактни показателя */}
      <div className="grid grid-cols-2 gap-2.5">
        <StatTile
          icon={<CalendarClock className="w-3.5 h-3.5" />}
          label="Остават дни"
          value={data.days_left != null ? `${data.days_left}` : "—"}
          suffix="дни"
          isDark={isDark}
          testid="gift-days-left"
        />
        <StatTile
          icon={<Zap className="w-3.5 h-3.5" />}
          label="Дневен лимит"
          value={data.daily_limit != null ? `${data.used_today} / ${data.daily_limit}` : "—"}
          suffix={data.daily_limit ? "днес" : ""}
          isDark={isDark}
          testid="gift-daily-usage"
        />
      </div>

      {pct <= 20 && (
        <div className={`mt-3 p-2.5 rounded-lg text-[12px] ${isDark ? "bg-rose-900/30 text-rose-200" : "bg-rose-100 text-rose-800"}`}>
          Остатъкът на подаръка Ви е под 20 %. Планирайте следващите стъпки
          или изберете редовен план, за да не прекъсвате.
        </div>
      )}
    </div>
  );
}

function StatTile({ icon, label, value, suffix, isDark, testid }) {
  const bg = isDark ? "bg-amber-950/40" : "bg-white/70";
  const t  = isDark ? "text-amber-100" : "text-amber-900";
  const s  = isDark ? "text-amber-400/70" : "text-amber-700";
  return (
    <div className={`rounded-lg p-2.5 ${bg}`} data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-[11px] ${s} mb-1`}>
        {icon} {label}
      </div>
      <div className={`text-lg font-bold ${t}`}>
        {value} <span className={`text-xs font-normal ${s}`}>{suffix}</span>
      </div>
    </div>
  );
}
